import { useState } from "react";
import { Bug, Lightbulb, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";

/**
 * Reusable feedback trigger. Renders a left-aligned footer strip with two
 * subtle buttons. Opens a shared modal regardless of which button was clicked.
 *
 * Props:
 *   variant: "section" (default — row for signed-in pages) | "link" (inline text link for landing footer)
 *   allowAnonymous: boolean — show the email field when the user isn't signed in
 */
export default function FeedbackButtons({ variant = "section", allowAnonymous = false }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug"); // "bug" | "suggestion"
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const openWith = (k) => {
    setKind(k);
    setMessage("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const msg = message.trim();
    if (msg.length < 3) {
      toast.error("Please add a bit more detail");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        type: kind,
        message: msg,
        page_url: typeof window !== "undefined" ? window.location.href : null,
      };
      if (allowAnonymous && email.trim()) payload.email = email.trim();
      await api.post("/feedback", payload);
      toast.success(kind === "bug" ? "Bug report sent — thank you!" : "Thanks for the suggestion!");
      setOpen(false);
      setMessage("");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send feedback");
    } finally {
      setBusy(false);
    }
  };

  const Trigger =
    variant === "link" ? (
      <button
        type="button"
        onClick={() => openWith("suggestion")}
        className="hover:text-slate-900 underline-offset-2 hover:underline"
        data-testid="feedback-link-footer"
      >
        Feedback
      </button>
    ) : (
      <div
        className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3"
        data-testid="feedback-strip"
      >
        <div className="text-xs text-slate-500">
          Have a problem or an idea? We read every message.
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => openWith("bug")}
            className="h-9 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="feedback-bug-btn"
          >
            <Bug className="w-3.5 h-3.5 mr-1.5" /> Report a bug
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => openWith("suggestion")}
            className="h-9 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="feedback-suggestion-btn"
          >
            <Lightbulb className="w-3.5 h-3.5 mr-1.5" /> Share a suggestion
          </Button>
        </div>
      </div>
    );

  const title = kind === "bug" ? "Report a bug" : "Share a suggestion";
  const Icon = kind === "bug" ? Bug : Lightbulb;
  const placeholder =
    kind === "bug"
      ? "What went wrong? Steps to reproduce, what you expected, what happened."
      : "What would make Quick Quotes work better for you?";

  return (
    <>
      {Trigger}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5 text-slate-700" />
              {title}
            </DialogTitle>
            <DialogDescription>
              {kind === "bug"
                ? "Tell us what broke so we can fix it fast."
                : "Tell us what to build next — we take requests seriously."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            {/* Type toggle inside modal — users can switch without closing */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setKind("bug")}
                className={`flex-1 h-9 rounded-md border text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                  kind === "bug"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid="feedback-type-bug"
              >
                <Bug className="w-3.5 h-3.5" /> Bug
              </button>
              <button
                type="button"
                onClick={() => setKind("suggestion")}
                className={`flex-1 h-9 rounded-md border text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                  kind === "suggestion"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid="feedback-type-suggestion"
              >
                <Lightbulb className="w-3.5 h-3.5" /> Suggestion
              </button>
            </div>

            {allowAnonymous && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Your email (optional)
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com — only if you'd like a reply"
                  autoComplete="off"
                  data-testid="feedback-email"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Message
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={placeholder}
                rows={5}
                maxLength={4000}
                required
                data-testid="feedback-message"
              />
              <div className="text-[11px] text-slate-400 mt-1 text-right">
                {message.length}/4000
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="feedback-cancel"
              >
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || message.trim().length < 3}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="feedback-submit"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {busy ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
